import _ from 'lodash';
import PropTypes from 'prop-types';
import React, { Component } from 'react';
import moment from 'moment';
import { Link } from 'react-router-dom';

import { withStyles } from '@material-ui/core/styles';
import Button from '@material-ui/core/Button';
import Grid from '@material-ui/core/Grid';
import Paper from '@material-ui/core/Paper';
import Typography from '@material-ui/core/Typography';

import TimelineIcon from '@material-ui/icons/Timeline';

import FMPStatusIcon from '../../components/ui/FMPStatusIcon';
import SystemContainer from '../../containers/system/System';
import { getStatus } from '../../modules/system/tankDailySalesStatus';

const styles = (theme) => ({
	innerPaper: {
		padding: 10,
		paddingTop: 5,
		textAlign: 'left',
		fontSize: 14,
		[theme.breakpoints.up('md')]: {
			padding: 40,
			paddingTop: 30,
		},
	},
	toolIcon: {
		color: '#777',
		verticalAlign: 'middle',
		marginRight: 8,
	},
	toolDescription: {
		color: '#666',
		marginBottom: 12,
	},
	label: {
		paddingRight: 5,
		textTransform: 'uppercase',
		fontSize: '85%',
		color: '#555',
	},
	healthReasons: {
		background: '#FFF8E1',
		border: '1px solid #FFE082',
		borderRadius: 4,
		padding: '8px 10px',
		margin: '10px 0px',
		fontSize: 13,
	},
	errorValue: {
		color: '#f44336',
	},
});

//- Minutes -> something a person reads without counting zeros.
function formatAgeMinutes(minutes) {
	if (minutes == null) {
		return 'never';
	}
	if (minutes < 60) {
		return `${minutes}m ago`;
	}
	const hours = Math.floor(minutes / 60);
	if (hours < 24) {
		return `${hours}h ${minutes % 60}m ago`;
	}
	return `${Math.floor(hours / 24)}d ${hours % 24}h ago`;
}

class SystemDashboard extends Component {
	constructor(props) {
		super(props);
		this.state = {
			salesStatus: undefined,
			isLoadingSalesStatus: true,
		};
	}

	componentDidMount() {
		getStatus({
			callback: (err, r) => {
				if (err) {
					this.setState({ isLoadingSalesStatus: false });
					return this.props.showErrors(_.isArray(err) ? err : [err]);
				}
				this.setState({ salesStatus: r, isLoadingSalesStatus: false });
			},
		});
	}

	renderRow = (label, value, isError) => {
		const { classes } = this.props;
		return (
			<div style={{ marginTop: 4 }} key={label}>
				<span className={classes.label}>{label}: </span>
				<span className={isError ? classes.errorValue : undefined}>{value}</span>
			</div>
		);
	};

	//- The one tool that exists so far gets a live summary rather than just a link, since
	//- "is it running" is the whole question somebody comes to this page with.
	renderSalesGenerationTool = () => {
		const { classes } = this.props;
		const { salesStatus, isLoadingSalesStatus } = this.state;

		return (
			<>
				<Typography variant="h5" align="left" gutterBottom style={{ position: 'relative' }}>
					<TimelineIcon className={classes.toolIcon} />
					Tableau Sales Data
					{salesStatus && (
						<span style={{ position: 'absolute', right: 0, top: 0 }}>
							<FMPStatusIcon
								status={salesStatus.healthSeverity}
								large
								title={salesStatus.health}
							/>
						</span>
					)}
				</Typography>

				<div className={classes.toolDescription}>
					Status of the job that materializes daily station sales into the reporting table Tableau
					reads from.
				</div>

				{isLoadingSalesStatus && <div>Loading generation status...</div>}
				{!isLoadingSalesStatus && !salesStatus && <div>Could not load the generation status.</div>}

				{salesStatus && (
					<>
						{!_.isEmpty(salesStatus.healthReasons) && (
							<div className={classes.healthReasons}>
								{salesStatus.healthReasons.map((reason) => (
									<div key={reason}>{reason}</div>
								))}
							</div>
						)}

						{this.renderRow(
							'Last Run',
							salesStatus.lastRun.lastRunAt
								? `${moment(salesStatus.lastRun.lastRunAt).format(
									'ddd, M/D h:mm a'
								)} (${formatAgeMinutes(salesStatus.lastRun.lastRunAgeMinutes)})`
								: 'Never'
						)}
						{this.renderRow(
							'Records Written',
							(salesStatus.lastRun.rowsUpserted || 0).toLocaleString()
						)}
						{this.renderRow(
							'Total Records',
							(salesStatus.coverage.rowCount || 0).toLocaleString()
						)}
						{this.renderRow(
							'Newest Sales Day',
							salesStatus.coverage.lastSaleDateWithVolume || '-'
						)}
						{salesStatus.lastRun.lastError &&
							this.renderRow('Last Error', salesStatus.lastRun.lastError, true)}
					</>
				)}

				<div style={{ marginTop: 20 }}>
					<Button
						color="primary"
						variant="outlined"
						component={Link}
						to="/system/tankDailySales"
					>
						View details
					</Button>
				</div>
			</>
		);
	};

	render() {
		const { classes } = this.props;

		return (
			<Grid container spacing={3}>
				<Grid item xs={12} md={6}>
					<Paper>
						<div className={classes.innerPaper}>{this.renderSalesGenerationTool()}</div>
					</Paper>
				</Grid>
				<Grid item xs={12} md={6}>
					<Paper>
						<div className={classes.innerPaper}>
							<Typography variant="h5" align="left" gutterBottom>
								System Tools
							</Typography>
							<div className={classes.toolDescription}>
								Operational status for the background jobs that keep reporting data current.
							</div>
							<div style={{ marginTop: 10 }}>
								<Button color="primary" component={Link} to="/system/tankDailySales">
									Tableau Sales Data Generation
								</Button>
							</div>
							<div>
								<Button color="primary" component={Link} to="/inventoryManagement/balanceSync">
									Inventory Balance Sync
								</Button>
							</div>
						</div>
					</Paper>
				</Grid>
			</Grid>
		);
	}
}

SystemDashboard.propTypes = {
	classes: PropTypes.object.isRequired,
	showErrors: PropTypes.func.isRequired,
};

export default _.flow([withStyles(styles), SystemContainer])(SystemDashboard);
